import React, { useEffect } from "react";
import { Container, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SimpleBox from "../../components/layout/container/SimpleBox";
import SelectInput from "../../components/input/select/SelectInput";
import { updateCurrentPersonId, updatePersonConfirmation } from "../../store/user";
import PersonData from "../../components/layout/person/PersonData";
import PageContent from "../../components/layout/container/PageContent";
import PersonMembership from "../../components/layout/person/PersonMembership";
import PersonEchelon from "../../components/layout/person/PersonEchelon";
import PersonPaymentReference from "../../components/layout/person/PersonPaymentReference";
import ShadowBox from "../../components/layout/container/ShadowBox";
import ContactForm from "../../components/form/ContactForm";
import { apiPublicRequests, apiPublicSeasonConfirm } from "../../api/public";
import NewMembership from "../../components/content/NewMembership";
import PersonRequests from "../../components/layout/person/PersonRequests";
import { toast } from "react-toastify";

const Membership = () => {

    const theme = useTheme();
    const dispatch = useDispatch();
    const currentPersonId = useSelector(state => state.user.currentPersonId);
    const impersonate = useSelector(state => state.user.impersonate);
    const people = useSelector(state => state.user.people);
    const uid = useSelector(state => state.user.uid);
    const displayName = useSelector(state => state.user.displayName);
    const userEmail = useSelector(state => state.user.email);

    const [requests, setRequests] = React.useState([]);

    // Hooks must run unconditionally on every render (Rules of Hooks) — the
    // "not logged in yet" early return below used to sit *before* this
    // effect, so useEffect was skipped on some renders and called on
    // others depending on `uid`, a real bug (react-hooks/rules-of-hooks),
    // not just a lint nit: a hook call order that can differ between
    // renders can corrupt React's internal state tracking for every other
    // hook in this component. Fixed by keeping the hook call itself
    // unconditional and moving the "skip when logged out" behavior inside
    // the effect body instead.
    useEffect(() => {
        if (!Boolean(uid)) return;
        apiPublicRequests({ impersonate }, (data, successFlag) => {
            if (successFlag > 0) {
                setRequests(data.people);
            }
        });
    }, [impersonate, uid]);

    if (!Boolean(uid)) return null;

    const person = (people ?? []).find(element => element.id === currentPersonId);
    const request = (requests ?? []).find(element => element.personId === currentPersonId);

    const contactEmail = Boolean(person) ? (
        person.email ?? (
            Boolean(person.emails) && person.emails.length > 0 ? person.emails[0] : null
        ) ?? person.paymentEmail
    ) : userEmail;
    const fullName = Boolean(person) ? person.fullName : displayName;
    const phoneNumber = Boolean(person) ? (person?.phoneNrMobilePrimary ?? person?.phoneNrMobileSecondary) : null;

    const onSeasonConfirmation = (personId) => {
        apiPublicSeasonConfirm({ personId }, (data, successFlag) => {
            if (successFlag > 0) {
                if (data.updated) {
                    dispatch(updatePersonConfirmation(personId));
                    toast.success("Obrigado por continuares connosco! A tua inscrição foi confirmada.");
                }
                else {
                    toast.error("Não foi possível confirmar a tua inscrição. Por favor, entra em contacto com a Direção da ADCB.");
                }
            }
        })
    }

    const showMembership = Boolean(person) && Boolean(person.membershipNumber);
    const showEchelon = Boolean(person) && Boolean(person.seasonPayments) && Object.keys(person.seasonPayments).length > 0;

    return (
        <PageContent showImageHeader={false}>
            <Container maxWidth="md">
                {
                    people.length > 1 ? (
                        <SimpleBox fullWidth={true} flexDirection="column" mt={6} alignItems="left">
                            <SelectInput
                                id="person"
                                label="Membro Ativo"
                                options={people.map(element => { return {id: element.id, label: element.fullName } })}
                                selected={currentPersonId}
                                onChange={(personId) => dispatch(updateCurrentPersonId(personId))} />
                            <Typography variant="body2" color="textSecondary" mt={2}>
                                Esta lista contém todos os membros associados ao email registado.
                                O membro ativo é a pessoa que será utilizada para todas as operações no site, especialmente na loja.
                            </Typography>
                        </SimpleBox>
                    ) : null
                }

                { Boolean(person) ? <PersonData personData={person} request={request} onSeasonConfirmation={onSeasonConfirmation} /> : null }

                { !Boolean(person) && (requests ?? []).length === 0 ? <NewMembership /> : null }

                { showMembership ? <PersonMembership payments={person.membershipPayments} /> : null}

                { showEchelon ? <PersonEchelon seasons={person.seasonPayments} /> : null}

                { Boolean(person) ? <PersonPaymentReference references={person.paymentReferences} personId={person.id} /> : null}

                <PersonRequests requests={requests} />

                <ShadowBox title="Contacto" mt={6}>
                    <ContactForm
                        fixedEmail={contactEmail}
                        fixedName={fullName}
                        fixedPhone={phoneNumber}
                        defaultTopic="Sócios"
                    />
                </ShadowBox>

            </Container>
        </PageContent>
    );
}

export default Membership;
